import React, { useState, useEffect } from 'react';
import { analyzeCard } from '../../services/cardAnalysisService';
import { CardAnalysisUpdate, AgentStep } from '../../types';

interface AnalysisProgressProps {
  cardId: string;
  onAnalysisComplete: (reportId: string) => void;
  onError: (error: Error) => void;
}

const AnalysisProgress: React.FC<AnalysisProgressProps> = ({ cardId, onAnalysisComplete, onError }) => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [steps, setSteps] = useState<AgentStep[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(true);

  useEffect(() => {
    let analyzeProcess: { abort: () => void } | null = null;
    
    const startAnalysis = () => {
      try {
        analyzeProcess = analyzeCard(cardId, handleUpdate);
      } catch (err) {
        const errorMessage = `Failed to start analysis: ${err instanceof Error ? err.message : 'Unknown error'}`;
        setError(errorMessage);
        setIsAnalyzing(false);
        onError(new Error(errorMessage));
      }
    };
    
    startAnalysis();
    
    return () => {
      if (analyzeProcess) {
        analyzeProcess.abort();
      }
    };
  }, [cardId]);
  
  const handleUpdate = (update: CardAnalysisUpdate) => {
    if (update.status === 'error') {
      const errorMessage = update.message || 'An error occurred during analysis';
      setError(errorMessage);
      setIsAnalyzing(false);
      onError(new Error(errorMessage));
      return;
    }
    
    if (update.data) {
      if (update.data.progress !== undefined) {
        setProgress(update.data.progress);
      }
      
      if (update.data.current_step) {
        setCurrentStep(update.data.current_step);
      }
      
      if (update.data.steps) {
        setSteps(update.data.steps);
      }
      
      if (update.status === 'complete' && update.data.report_id) {
        setIsAnalyzing(false);
        onAnalysisComplete(update.data.report_id);
      }
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Analysis Progress</h2>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="mb-4">
        <div className="flex justify-between mb-1">
          <span className="text-base font-medium text-blue-700">Progress</span>
          <span className="text-sm font-medium text-blue-700">{progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${progress}%` }}></div>
        </div>
      </div>
      
      {currentStep && (
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700">Current Step: {currentStep}</p>
        </div>
      )}
      
      {steps.length > 0 && (
        <div className="mb-4">
          <h3 className="text-lg font-medium mb-2">Steps</h3>
          <ul className="space-y-2">
            {steps.map((step, index) => (
              <li key={index} className="border-l-2 pl-3 py-1" style={{ 
                borderColor: 
                  step.status === 'completed' ? 'green' : 
                  step.status === 'in-progress' ? 'blue' : 'gray'
              }}>
                <p className="font-medium">{step.description}</p>
                {step.result && <p className="text-sm text-gray-600">{step.result}</p>}
                <span className={`text-xs px-2 py-1 rounded ${
                  step.status === 'completed' ? 'bg-green-100 text-green-800' : 
                  step.status === 'in-progress' ? 'bg-blue-100 text-blue-800' : 
                  'bg-gray-100 text-gray-800'
                }`}>
                  {step.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {isAnalyzing && (
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <span className="ml-2">Analyzing...</span>
        </div>
      )}
    </div>
  );
};

export default AnalysisProgress;
